import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronRight, Star } from 'lucide-react';
import './Home.css';
import specialtyService from '../../services/specialtyService';
import salonService from '../../services/salonService';
import AdCarousel from '../../components/AdCarousel/AdCarousel';
import { getCategoryImage } from '../../constants/categoryImages';
import { getSalonImage, SALON_FALLBACK_IMAGE } from '../../constants/salonImages';

function Home() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [quartier, setQuartier] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [salons, setSalons] = useState([]);
  const [salonsTotal, setSalonsTotal] = useState(0);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [loadingSalons, setLoadingSalons] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoadingSalons(true);
      setLoadingSpecialties(true);
      const [salonsRes, specData] = await Promise.all([
        salonService.getAllSalons({ limit: 20, withMeta: true }),
        specialtyService.getAllSpecialties(),
      ]);
      setSalons(salonsRes?.salons || []);
      setSalonsTotal(salonsRes?.total || salonsRes?.salons?.length || 0);
      setSpecialties(specData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSalons(false);
      setLoadingSpecialties(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('q', searchTerm.trim());
    if (quartier.trim()) params.set('city', quartier.trim());
    navigate(`/salons?${params.toString()}`);
  };

  const popular = useMemo(() => {
    return [...salons]
      .sort((a, b) => parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0))
      .slice(0, 8);
  }, [salons]);

  return (
    <div className="home">
      <section className="home-hero">
        <div className="home-hero-glow home-hero-glow-a" />
        <div className="home-hero-glow home-hero-glow-b" />

        <div className="section-container home-hero-inner">
          <div className="home-top-row">
            <div>
              <h1 className="home-brand">Shynea</h1>
              <p className="home-tagline">Réservez votre beauté à Dakar</p>
            </div>
          </div>

          <div className="home-search-glass">
            <div className="home-search-field">
              <Search size={18} strokeWidth={2} className="home-search-ico" />
              <input
                type="text"
                placeholder="Salon, soin, quartier…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="home-search-divider" />
            <div className="home-search-field home-search-city">
              <MapPin size={18} strokeWidth={2} className="home-search-ico" />
              <input
                type="text"
                placeholder="Plateau, Almadies…"
                value={quartier}
                onChange={(e) => setQuartier(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <button type="button" className="home-search-cta" onClick={handleSearch}>
              Rechercher
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Publicités — toujours visible, juste sous la recherche */}
      <section className="home-section ads-section" aria-label="Publicités">
        <div className="section-container">
          <h2 className="sec-title ads-title">Publicités</h2>
          <AdCarousel
            onSlideClick={(slide) => navigate(`/salons?cat=${slide.id}`)}
          />
        </div>
      </section>

      <section className="home-section popular-section">
        <div className="section-container">
          <div className="sec-row">
            <h2 className="sec-title">Populaires</h2>
            <button
              type="button"
              className="sec-link"
              onClick={() => navigate('/salons')}
            >
              Voir tout
              <ChevronRight size={14} />
            </button>
          </div>

          {loadingSalons ? (
            <div className="popular-scroll">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="popular-skeleton" />
              ))}
            </div>
          ) : popular.length === 0 ? (
            <p className="home-empty-hint">Aucun salon pour le moment</p>
          ) : (
            <div className="popular-scroll">
              {popular.map((salon) => {
                const rating = parseFloat(salon.average_rating || 0);
                const img = getSalonImage(salon);
                const isLogo = img === SALON_FALLBACK_IMAGE;
                const specialty =
                  Array.isArray(salon.specialties) && salon.specialties[0]
                    ? salon.specialties[0].name || salon.specialties[0]
                    : null;
                return (
                  <button
                    key={salon.id}
                    type="button"
                    className="popular-card"
                    onClick={() => navigate(`/salon/${salon.id}`)}
                  >
                    <div
                      className={`popular-bg${isLogo ? ' popular-bg--logo' : ''}`}
                      style={{ backgroundImage: `url(${img})` }}
                    />
                    <div className="popular-grad" />
                    {specialty && (
                      <span className="popular-tag">{specialty}</span>
                    )}
                    <div className="popular-bottom">
                      <div className="popular-info">
                        <span className="popular-name">{salon.name}</span>
                        <span className="popular-loc">
                          <MapPin size={11} />
                          {salon.city || 'Dakar'}
                        </span>
                      </div>
                      {rating > 0 && (
                        <span className="popular-rating">
                          <Star size={12} fill="#FCD34D" color="#FCD34D" />
                          {rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="home-section promo-section">
        <div className="section-container">
          <button
            type="button"
            className="promo-banner"
            onClick={() => navigate('/salons')}
            style={{
              backgroundImage: `url(${
                salons[2] ? getSalonImage(salons[2]) : SALON_FALLBACK_IMAGE
              })`,
            }}
          >
            <div className="promo-overlay">
              <div className="promo-copy">
                <span className="promo-title">Réservez en un instant</span>
                <span className="promo-sub">
                  {salonsTotal || salons.length} salons près de vous
                </span>
              </div>
              <span className="promo-arrow">
                <ChevronRight size={20} />
              </span>
            </div>
          </button>
        </div>
      </section>

      <section className="home-section explorer-section">
        <div className="section-container">
          <div className="sec-row">
            <h2 className="sec-title">Explorer</h2>
            <span className="count-pill">
              {specialties.length} catégorie{specialties.length > 1 ? 's' : ''}
            </span>
          </div>

          {loadingSpecialties ? (
            <div className="explorer-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="explorer-skeleton" />
              ))}
            </div>
          ) : specialties.length === 0 ? (
            <p className="home-empty-hint">Aucune catégorie</p>
          ) : (
            <div className="explorer-grid">
              {specialties.map((sp) => (
                <button
                  key={sp.id}
                  type="button"
                  className="explorer-card"
                  onClick={() =>
                    navigate(
                      `/salons?specialty=${sp.id}&name=${encodeURIComponent(sp.name)}`
                    )
                  }
                >
                  <img
                    src={getCategoryImage(sp.name)}
                    alt=""
                    className="explorer-img"
                  />
                  <div className="explorer-grad" />
                  <div className="explorer-copy">
                    <span className="explorer-name">{sp.name}</span>
                    <span className="explorer-cta">
                      Voir les salons
                      <ChevronRight size={12} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Home;
